import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, ChevronDown, FileText, Activity } from 'lucide-react';
import { api } from '../../../lib/api';

interface QueueItem {
  id: string;
  fichier_nom: string;
  processing_step: string | null;
  statut: string;
}

interface QueueData {
  processing: QueueItem[];
  pending: QueueItem[];
  completed_recent: QueueItem[];
  counts: { processing: number; pending: number; completed: number; errored: number };
}

const STEP_LABELS: Record<string, string> = {
  thumbnail: 'Thumbnail…',
  ocr: 'OCR…',
  classification: 'Classification…',
  completed: 'Terminé',
  erreur: 'Erreur',
};

export function ProcessingQueueBadge() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState<QueueData | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    const poll = () => {
      api.getZdProcessingQueue().then(r => {
        if (active && r.success && r.data) setQueue(r.data);
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!queue) return null;

  const totalActive = queue.counts.processing + queue.counts.pending;
  if (totalActive === 0 && queue.counts.completed === 0 && queue.counts.errored === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg hover:bg-neutral-100 dark:hover:bg-gray-800 transition-colors"
        title="File de traitement"
      >
        {totalActive > 0 ? (
          <Loader2 size={16} className="text-amber-500 animate-spin" />
        ) : queue.counts.errored > 0 ? (
          <AlertCircle size={16} className="text-red-500" />
        ) : (
          <CheckCircle size={16} className="text-emerald-500" />
        )}
        {totalActive > 0 && (
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{totalActive}</span>
        )}
        <ChevronDown size={12} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
              Traitement en cours
            </span>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {queue.counts.completed > 0 && (
                <span className="flex items-center gap-1"><CheckCircle size={11} className="text-emerald-500" />{queue.counts.completed}</span>
              )}
              {queue.counts.errored > 0 && (
                <span className="flex items-center gap-1"><AlertCircle size={11} className="text-red-500" />{queue.counts.errored}</span>
              )}
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {/* Processing items */}
            {queue.processing.map(item => (
              <div key={item.id} className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => { setOpen(false); navigate(`/zillodocs?doc=${item.id}`); }}>
                <Loader2 size={14} className="text-amber-500 animate-spin shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{item.fichier_nom}</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">{STEP_LABELS[item.processing_step || ''] || item.processing_step}</p>
                </div>
              </div>
            ))}

            {/* Pending items */}
            {queue.pending.slice(0, 5).map(item => (
              <div key={item.id} className="px-3 py-2 flex items-center gap-2 opacity-60">
                <FileText size={14} className="text-gray-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{item.fichier_nom}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">En attente</p>
                </div>
              </div>
            ))}
            {queue.pending.length > 5 && (
              <div className="px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500 text-center">
                +{queue.pending.length - 5} en attente
              </div>
            )}

            {/* Recently completed */}
            {queue.completed_recent.slice(0, 3).map(item => (
              <div key={item.id} className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => { setOpen(false); navigate(`/zillodocs?doc=${item.id}`); }}>
                <CheckCircle size={14} className="text-emerald-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{item.fichier_nom}</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">Traité</p>
                </div>
              </div>
            ))}

            {/* Empty state */}
            {queue.processing.length === 0 && queue.pending.length === 0 && queue.completed_recent.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
                Aucun traitement récent
              </div>
            )}
          </div>

          {/* Footer — link to activity page */}
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => { setOpen(false); navigate('/zillodocs/activity'); }}
              className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium"
            >
              <Activity size={12} />
              Voir toute l'activité
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
